import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp } from '../../firebase/authService';
import { carrierApi } from '../../firebase/functionsService';
import { useAuth } from '../../contexts/AuthContext';
import { useEnhetsregister } from '../../hooks/useEnhetsregister';
import { normalizeNorwegianPhone } from '../../utils/phone';
import { cleanDigits, isValidOrgNumber } from '../../utils/invoiceProfile';
import Field from '../carrier/Field';
import { ui } from '../carrier/carrierStyles';

// Registrering av et transportfirma: oppretter kontoen (rolle carrier) og firmaet i ett steg.
// Funksjonen createCarrierOrg avviser når settings/platform.carrierOrgEnabled er av.
export default function CarrierRegister() {
  const navigate = useNavigate();
  const { refreshUserDoc } = useAuth();
  const { orgData, orgError, orgLoading, validateOrgNumber } = useEnhetsregister();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', orgNumber: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setMessage('');
    const orgNumber = cleanDigits(form.orgNumber);
    const phone = normalizeNorwegianPhone(form.phone);
    const found = {};
    if (form.name.trim().length < 2) found.name = 'Skriv inn navn';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) found.email = 'Skriv inn en gyldig e-postadresse';
    if (!phone) found.phone = 'Skriv inn et norsk mobilnummer';
    if (form.password.length < 6) found.password = 'Passordet må ha minst 6 tegn';
    if (!isValidOrgNumber(orgNumber)) found.orgNumber = 'Ugyldig organisasjonsnummer (9 siffer)';
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    try {
      const org = orgData?.organisasjonsnummer === orgNumber ? orgData : await validateOrgNumber(orgNumber);
      if (!org) return;
      await signUp({
        email: form.email.trim(), password: form.password, role: 'carrier',
        name: form.name.trim(), phone, orgNumber, orgName: org.navn,
      });
      try {
        await carrierApi.createCarrierOrg();
        await refreshUserDoc();
        navigate('/transportor/innstillinger');
      } catch (orgErr) {
        // Kontoen finnes. Dashboardet tilbyr å opprette firmaet på nytt når funksjonen åpnes.
        await refreshUserDoc();
        navigate('/transportor', { state: { error: orgErr?.message } });
      }
    } catch (err) {
      setMessage(err?.code === 'auth/email-already-in-use'
        ? 'E-postadressen er allerede registrert. Logg inn i stedet.'
        : 'Kunne ikke opprette kontoen. Prøv igjen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main style={{ ...ui.main, maxWidth: 480 }}>
      <h1 style={ui.h1}>Registrer transportfirma</h1>
      <form onSubmit={submit} noValidate style={ui.card}>
        <Field id="cr-name" label="Ditt navn" error={errors.name}>
          <input id="cr-name" style={ui.input} value={form.name} onChange={set('name')} autoComplete="name" />
        </Field>
        <Field id="cr-email" label="E-post" error={errors.email}>
          <input id="cr-email" type="email" style={ui.input} value={form.email} onChange={set('email')} autoComplete="email" />
        </Field>
        <Field id="cr-phone" label="Mobilnummer" error={errors.phone}>
          <input id="cr-phone" style={ui.input} value={form.phone} onChange={set('phone')} inputMode="tel" autoComplete="tel" />
        </Field>
        <Field id="cr-password" label="Passord" error={errors.password}>
          <input id="cr-password" type="password" style={ui.input} value={form.password} onChange={set('password')} autoComplete="new-password" />
        </Field>
        <Field id="cr-org" label="Organisasjonsnummer" error={errors.orgNumber || orgError}>
          <input id="cr-org" style={ui.input} value={form.orgNumber} onChange={set('orgNumber')} onBlur={() => { if (isValidOrgNumber(form.orgNumber)) validateOrgNumber(cleanDigits(form.orgNumber)); }} inputMode="numeric" />
        </Field>
        {orgLoading && <p style={ui.hint}>Slår opp i Enhetsregisteret…</p>}
        {orgData && <p style={ui.hint}>{orgData.navn}</p>}
        {message && <p role="alert" style={ui.error}>{message}</p>}
        <button type="submit" className="tippi-btn" style={{ ...ui.btn, marginTop: 14, width: '100%' }} disabled={busy}>
          {busy ? 'Oppretter…' : 'Opprett konto og firma'}
        </button>
      </form>
      <p style={ui.hint}>Har du allerede konto? <Link to="/login" style={ui.link}>Logg inn</Link></p>
    </main>
  );
}
